/**
 * Chakiboo Pack Availability Guard
 *
 * Sits on top of chakiboo-pack-selector.js and fixes two things:
 *
 * 1. Graphic (and Color) tiles were rendered from the full option list, so
 *    "Hidden Biplane" showed at sizes 7-14 and "Hidden Skateboarder" showed at
 *    sizes 2-6, even though no such variant exists. Tiles with no variant for
 *    the pair's chosen size are now removed; tiles whose variant exists but is
 *    sold out are greyed out.
 *
 * 2. The Add to cart button took its state from Shopify's single-variant
 *    logic, which only ever sees the Step 1 size. Choosing size 12 inside a
 *    Pair left Step 1 on its default size, so the button read "Unavailable"
 *    for a combination that is in stock. The pack selector now owns the button
 *    and validates every active pair against the real variant list.
 */
(function () {
  'use strict';

  if (window.__chakibooPackAvailability) return;
  window.__chakibooPackAvailability = true;

  /* Set to false to grey out impossible graphics instead of removing them. */
  var HIDE_MISSING = true;

  /* Shown when no buyable variant matches the configured pairs. */
  var UNAVAILABLE_LABEL = 'Finish Your Selections';

  /*
   * How long to wait for chakiboo-pack-selector.js to finish initialising.
   * This page is heavy, so the window is generous, and the wait uses setTimeout
   * rather than requestAnimationFrame, which browsers throttle hard under load
   * and in background tabs. 300 x 100ms = 30s.
   */
  var ATTACH_POLL_MS = 100;
  var ATTACH_MAX_TRIES = 300;

  var OPTION_KEYS = ['size', 'color', 'graphic'];

  var EVENTS = [
    'chakiboo:pair-selection-change',
    'chakiboo:pack-change',
    'chakiboo:pair-size-change',
    'chakiboo:pair-color-change',
    'chakiboo:pair-graphic-change',
  ];

  function optionIndex(el, key) {
    var indexes = el.variantData && el.variantData.optionIndexes;
    var i = indexes ? indexes[key] : -1;
    return i == null || i < 0 ? -1 : i;
  }

  function optionValue(el, variant, key) {
    var i = optionIndex(el, key);
    if (i === 0) return variant.option1;
    if (i === 1) return variant.option2;
    if (i === 2) return variant.option3;
    return null;
  }

  /** Every variant matching a partial {size, color, graphic} selection. */
  function matchingVariants(el, selection) {
    var list = (el.variantData && el.variantData.variants) || [];
    return list.filter(function (variant) {
      for (var n = 0; n < OPTION_KEYS.length; n++) {
        var key = OPTION_KEYS[n];
        var wanted = selection[key];
        if (!wanted) continue;
        if (optionIndex(el, key) < 0) continue;
        if (optionValue(el, variant, key) !== wanted) return false;
      }
      return true;
    });
  }

  /** 'ok' | 'soldout' | 'missing' */
  function combinationState(el, selection) {
    var found = matchingVariants(el, selection);
    if (!found.length) return 'missing';
    for (var n = 0; n < found.length; n++) {
      if (found[n].available !== false) return 'ok';
    }
    return 'soldout';
  }

  /*
   * Every write here is conditional. Re-asserting a boolean attribute that is
   * already set still emits a MutationRecord, and the DOM watcher below listens
   * for `disabled`, so unconditional writes would feed it in a loop.
   */
  function setTileState(button, state) {
    var unavailable = state !== 'ok';
    var hide = HIDE_MISSING && state === 'missing';
    var disable = hide || unavailable;
    var greyed = unavailable && !hide;
    var aria = disable ? 'true' : 'false';

    if (button.hidden !== hide) button.hidden = hide;

    if (hide) {
      if (button.getAttribute('aria-hidden') !== 'true') button.setAttribute('aria-hidden', 'true');
    } else if (button.hasAttribute('aria-hidden')) {
      button.removeAttribute('aria-hidden');
    }

    if (button.disabled !== disable) button.disabled = disable;
    if (button.classList.contains('is-unavailable') !== greyed) {
      button.classList.toggle('is-unavailable', greyed);
    }
    if (button.getAttribute('aria-disabled') !== aria) button.setAttribute('aria-disabled', aria);
  }

  /** True for the "No Graphic" tile, which exists in every size. */
  function isNoneTile(button) {
    if (button.classList.contains('chakiboo-pack__graphic-option--none')) return true;
    var value = (button.dataset.optionValue || '').toLowerCase().trim();
    return value.indexOf('no graphic') > -1 || value.indexOf('no design') > -1 || value === 'none';
  }

  /**
   * If the current value is impossible for this pair, fall back to a buyable
   * one. A shopper who chose a hidden graphic keeps a hidden graphic across a
   * size change (Biplane runs 2-6, Skateboarder runs 7-14), so "No Graphic" is
   * only used when nothing else is buyable.
   */
  function repairValue(el, pairEl, selector, current, build) {
    if (current && combinationState(el, build(current)) === 'ok') return current;

    var buttons = Array.prototype.slice.call(pairEl.querySelectorAll(selector));
    var currentWasNone = false;
    var fallback = null;

    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].dataset.optionValue === current) currentWasNone = isNoneTile(buttons[i]);
    }

    for (var n = 0; n < buttons.length; n++) {
      var value = buttons[n].dataset.optionValue;
      if (!value || combinationState(el, build(value)) !== 'ok') continue;
      if (fallback === null) fallback = value;
      if (isNoneTile(buttons[n]) === currentWasNone) return value;
    }

    return fallback === null ? current : fallback;
  }

  function paintGroup(el, pairEl, selector, build) {
    pairEl.querySelectorAll(selector).forEach(function (button) {
      var value = button.dataset.optionValue;
      if (!value) return;
      setTileState(button, combinationState(el, build(value)));
    });
  }

  function paintPair(el, pairEl) {
    var pairIndex = Number(pairEl.dataset.chakibooPair);
    var selection = el.pairSelections && el.pairSelections[pairIndex];
    if (!pairIndex || !selection) return;

    var sizeSelect = pairEl.querySelector('[data-chakiboo-pair-size]');
    if (sizeSelect && optionIndex(el, 'size') >= 0) {
      Array.prototype.forEach.call(sizeSelect.options, function (option) {
        var disable = combinationState(el, { size: option.value }) !== 'ok';
        if (option.disabled !== disable) option.disabled = disable;
      });
    }

    selection.color = repairValue(el, pairEl, '[data-chakiboo-pair-color]', selection.color, function (value) {
      return { size: selection.size, color: value };
    });

    selection.graphic = repairValue(el, pairEl, '[data-chakiboo-pair-graphic]', selection.graphic, function (value) {
      return { size: selection.size, color: selection.color, graphic: value };
    });

    paintGroup(el, pairEl, '[data-chakiboo-pair-color]', function (value) {
      return { size: selection.size, color: value };
    });

    paintGroup(el, pairEl, '[data-chakiboo-pair-graphic]', function (value) {
      return { size: selection.size, color: selection.color, graphic: value };
    });

    if (typeof el.renderPairUi === 'function') el.renderPairUi(pairIndex);
  }

  /*
   * The live button label is <span class="custom-add-to-cart">Add to Cart<img
   * class="custom-add-to-cart__arrow"></span>. Writing textContent would delete
   * the arrow image, so only the text node is ever touched.
   */
  function findLabel(submit) {
    return submit.querySelector('.custom-add-to-cart') || submit.querySelector(':scope > span');
  }

  function labelTextNode(labelEl) {
    for (var i = 0; i < labelEl.childNodes.length; i++) {
      var node = labelEl.childNodes[i];
      if (node.nodeType === 3 && node.nodeValue.trim()) return node;
    }
    return null;
  }

  function readLabel(labelEl) {
    var node = labelTextNode(labelEl);
    return node ? node.nodeValue.trim() : '';
  }

  function writeLabel(labelEl, text) {
    var node = labelTextNode(labelEl);
    if (node) {
      if (node.nodeValue.trim() !== text) node.nodeValue = text;
      return;
    }
    labelEl.insertBefore(document.createTextNode(text), labelEl.firstChild);
  }

  function getSubmit(el) {
    var info = el.closest('product-info');
    var form = info ? info.querySelector('product-form') : null;
    return form ? { form: form, submit: form.querySelector('[type="submit"][name="add"]') } : null;
  }

  function paintCta(el) {
    var found = getSubmit(el);
    if (!found || !found.submit) return;
    var form = found.form;
    var submit = found.submit;

    var packCount = Number(el.dataset.selectedPack) || 1;
    var active = typeof el.getActivePairSelections === 'function' ? el.getActivePairSelections() : {};
    var keys = Object.keys(active);
    var buyable = keys.length === packCount;
    var firstVariantId = null;

    if (buyable && typeof el.resolveVariantForPair === 'function') {
      for (var n = 0; n < keys.length; n++) {
        var result = el.resolveVariantForPair(Number(keys[n]), active[keys[n]]);
        if (!result || result.error || !result.variant) {
          buyable = false;
          break;
        }
        if (firstVariantId === null) firstVariantId = result.variant.id;
      }
    }

    if (submit.classList.contains('loading')) return;

    /*
     * Only write when the value actually differs. The DOM watcher below sees
     * these attributes, so an unconditional write would re-trigger it forever.
     */
    var ariaValue = buyable ? 'false' : 'true';
    if (submit.disabled !== !buyable) submit.disabled = !buyable;
    if (submit.getAttribute('aria-disabled') !== ariaValue) {
      submit.setAttribute('aria-disabled', ariaValue);
    }

    var label = findLabel(submit);
    if (label) {
      writeLabel(label, buyable ? el.__chakibooBuyLabel || 'Add to Cart' : UNAVAILABLE_LABEL);

       label.classList.toggle('is-finish-selection', !buyable);
    }

    /* Absent on the landing page, which renders no Buy now button. */
    var buyNow = form.querySelector('.buy-now-button');
    if (buyNow) {
      if (buyNow.disabled !== !buyable) buyNow.disabled = !buyable;
      if (buyNow.getAttribute('aria-disabled') !== ariaValue) {
        buyNow.setAttribute('aria-disabled', ariaValue);
      }
    }

    /* Safety net: keep the native form pointing at a real variant. */
    var idInput = form.querySelector('.product-variant-id');
    if (idInput && firstVariantId) {
      idInput.value = String(firstVariantId);
      idInput.disabled = false;
    }
  }

  function refresh(el) {
    if (!el || !el.variantData || !el.pairSelections) return false;
    (el.pairItems || []).forEach(function (pairEl) {
      paintPair(el, pairEl);
    });
    paintCta(el);
    return true;
  }

  function safeRefresh(el) {
    try {
      refresh(el);
    } catch (error) {
      console.error('[chakiboo-availability] refresh failed', error);
    }
  }

  /*
   * Coalesce bursts, but do not wait for an animation frame. This page is heavy
   * enough that requestAnimationFrame callbacks were observed arriving seconds
   * late during load, and they do not run at all in a hidden tab - which left
   * the unfixed tiles on screen. A repaint costs ~0.001 ms, so there is nothing
   * to gain by aligning it to a frame.
   */
  function schedule(el) {
    if (el.__chakibooAvailabilityQueued) return;
    el.__chakibooAvailabilityQueued = true;
    window.setTimeout(function () {
      el.__chakibooAvailabilityQueued = false;
      safeRefresh(el);
    }, 0);
  }

  function attach(el, attempt) {
    if (el.__chakibooAvailabilityBound) return;

    /* Wait for chakiboo-pack-selector.js to finish connectedCallback. */
    if (!el.variantData || !el.pairSelections) {
      if ((attempt || 0) >= ATTACH_MAX_TRIES) return;
      window.setTimeout(function () {
        attach(el, (attempt || 0) + 1);
      }, ATTACH_POLL_MS);
      return;
    }

    el.__chakibooAvailabilityBound = true;
    /* Diagnostic: how long after navigation the guard took effect. */
    try { el.__chakibooBoundAt = Math.round(performance.now()); } catch (e) {}

    /*
     * Remember the theme's own button wording ("Add to Cart", capital C) rather
     * than hardcoding it, so the copy is unchanged when the button is restored.
     * Only trust it if the button rendered enabled; a disabled button already
     * says "Sold out" or "Unavailable".
     */
    var found = getSubmit(el);
    var initialLabel = found && found.submit ? findLabel(found.submit) : null;
    if (initialLabel && found.submit && !found.submit.disabled) {
      el.__chakibooBuyLabel = readLabel(initialLabel) || 'Add to Cart';
    } else {
      el.__chakibooBuyLabel = 'Add to Cart';
    }

    /*
     * Stop mirroring a single pair's Color/Graphic into Shopify's hidden
     * option inputs. That mirror is what made the CTA resolve against the
     * Step 1 size instead of the pair's own size.
     */
    el.mirrorPairToDawn = function () {};

    EVENTS.forEach(function (name) {
      el.addEventListener(name, function () {
        schedule(el);
      });
    });

    var info = el.closest('product-info');
    if (info) {
      info.addEventListener('change', function (event) {
        var target = event.target;
        if (!target || typeof target.matches !== 'function') return;
        if (target.matches('[data-chakiboo-global-size], [data-chakiboo-pair-size]')) schedule(el);
      });
    }

    /* Paint synchronously: the first paint is what removes the wrong tiles. */
    safeRefresh(el);
  }

  /*
   * Bind new selectors, and re-check ones already bound. The re-check matters:
   * the theme's own single-variant logic disables the button and rewrites its
   * label asynchronously, and it may swap the <product-form> node out entirely.
   * Both land here as DOM mutations, so this is what puts the button back.
   */
  function scan() {
    document.querySelectorAll('chakiboo-pack-selector').forEach(function (el) {
      if (el.__chakibooAvailabilityBound) schedule(el);
      else attach(el, 0);
    });
  }

  /*
   * Coalesce to one scan per task. Other scripts on the page (popups, review
   * widgets) mutate the DOM constantly; without this the observer below would
   * run a document query on every one of those batches.
   */
  var scanQueued = false;
  function scanSoon() {
    if (scanQueued) return;
    scanQueued = true;
    window.setTimeout(function () {
      scanQueued = false;
      scan();
    }, 0);
  }

  /*
   * Bind as early as possible, from every signal available: now, when the
   * custom element class is registered, at DOMContentLoaded, and at load.
   * Until the guard binds, the page shows the unfixed tiles, so being early
   * matters more than being tidy. attach() is idempotent.
   */
  scan();

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('chakiboo-pack-selector').then(scan, function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  }
  window.addEventListener('load', scan);

  /*
   * The theme re-renders the product section, and rewrites the button, in three
   * different shapes: replacing nodes (childList), assigning textContent to the
   * label (childList) or its nodeValue (characterData), and toggling `disabled`.
   * All three must put the button back, so all three are watched. Writes above
   * are conditional, so a settled page produces no further mutations.
   */
  if (window.MutationObserver) {
    new MutationObserver(scanSoon).observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }
})();
